import { FileText } from 'lucide-react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import type { VerticalCoiRequest } from '../content/insuranceVerticals'

function MessageHeader({ fromCai, sender, to }: { fromCai: boolean; sender: string; to: string }) {
  return (
    <div className="flex items-start gap-3">
      <Avatar className="size-9">
        <AvatarFallback
          className={cn(
            'text-sm font-extrabold',
            fromCai ? 'bg-ink-900 text-brand-400' : 'bg-ink-100 text-ink'
          )}
        >
          {fromCai ? 'C' : sender[0]}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-bold">{sender}</span>
          <span className="text-ink-500 ml-auto text-xs">9:14 AM</span>
        </div>
        <div className="text-ink-500 truncate text-xs">{to}</div>
      </div>
    </div>
  )
}

/** Looping email thread: a customer asks for a certificate holder, Cai replies with the COI attached. */
export function CoiRequestVisual({ request }: { request: VerticalCoiRequest }) {
  const { requesterName, requesterEmail, certificateHolder, businessName } = request
  return (
    <div
      className="text-ink flex w-full flex-col gap-4.5 rounded-sm bg-white px-7 pt-7 pb-8"
      role="img"
      aria-label={`${requesterName} emails Cai asking to add ${certificateHolder} as a certificate holder, and Cai replies with the COI attached.`}
    >
      {/* Keyframes ship with the component — marketing.css only loads under MarketingLayout. */}
      <style>
        {`
          @keyframes coi-request { 0%,4% { opacity: 0; transform: translateY(16px) } 10%,92% { opacity: 1; transform: none } 97%,100% { opacity: 0 } }
          @keyframes coi-reply { 0%,36% { opacity: 0; transform: translateY(20px) } 44%,92% { opacity: 1; transform: none } 97%,100% { opacity: 0 } }
          @keyframes coi-attachment { 0%,48% { opacity: 0; transform: scale(.94) } 55%,92% { opacity: 1; transform: none } 97%,100% { opacity: 0 } }
        `}
      </style>
      <div aria-hidden="true" className="text-[19px] font-bold tracking-[-0.01em]">
        Certificate holder request
      </div>
      <div
        aria-hidden="true"
        className="border-ink-100 flex animate-[coi-request_9s_ease-out_infinite] flex-col gap-3 border-b pb-4.5 motion-reduce:animate-none"
      >
        <MessageHeader fromCai={false} sender={requesterName} to="to requests@cai.ai" />
        <p className="pl-12 text-sm leading-[1.55]">
          Can you add {certificateHolder} as a certificate holder and send them a copy? Thanks
        </p>
      </div>
      <div
        aria-hidden="true"
        className="flex animate-[coi-reply_9s_ease-out_infinite] flex-col gap-3 motion-reduce:animate-none"
      >
        <MessageHeader fromCai sender="requests@cai.ai" to={`to ${requesterEmail}, ${certificateHolder}`} />
        <div className="flex flex-col gap-2.5 pl-12 text-sm leading-[1.55]">
          <p>Attached is the Certificate of insurance (COI) for {businessName}.</p>
          <p>Cai Insurance Services</p>
          <div className="border-ink-100 bg-ink-50 flex w-fit origin-left animate-[coi-attachment_9s_ease-out_infinite] items-center gap-3 rounded-sm border py-2.5 pr-4 pl-3 motion-reduce:animate-none">
            <FileText className="size-5.5" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-semibold">COI - {businessName.toUpperCase()}</span>
              <span className="text-ink-500 font-mono text-[11px]">PDF</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
